import Api from '../../../Services/Api';
import { format } from 'date-fns';
import { facebookAccountService } from './facebookAccountService';
import {
  fetchAdSenseRevenue,
  fetchFacebookCost,
  fetchCompadoRevenue,
  combineAllData,
  createExportPayload,
} from '../AnalyticsHelpers';

/**
 * Service module for campaign data operations
 */

/**
 * Fetch Facebook adset budgets for campaigns that have null budget values
 */
export const fetchFacebookAdsetBudgets = async (campaignIds) => {
  try {
    if (!campaignIds || !campaignIds.length) {
      console.log('No campaign IDs provided for adset budget lookup');
      return { success: true, data: {} };
    }

    console.log(`Fetching adset budgets for ${campaignIds.length} Facebook campaigns`);

    const adsetBudgets = {};
    const batchSize = 5;
    const batches = [];

    for (let i = 0; i < campaignIds.length; i += batchSize) {
      batches.push(campaignIds.slice(i, i + batchSize));
    }

    for (const batch of batches) {
      const batchPromises = batch.map(campaignId =>
        Api.getFacebookAdsetBudgets(campaignId)
          .then(response => {
            if (response.success && response.data) {
              adsetBudgets[campaignId] = response.data;
            }
            return response;
          })
          .catch(error => {
            console.error(`Error fetching adset budget for campaign ${campaignId}:`, error);
            return null;
          })
      );

      await Promise.all(batchPromises);
    }

    console.log(`Successfully fetched adset budgets for ${Object.keys(adsetBudgets).length} campaigns`);
    return { success: true, data: adsetBudgets };
  } catch (error) {
    console.error('Error fetching Facebook adset budgets:', error);
    return { success: false, error: error.message || 'Error fetching adset budgets' };
  }
};

/**
 * Fetch Facebook campaigns data from all accounts
 */
export const fetchFacebookCampaigns = async (forceRefresh = false, account_id = null) => {
  try {
    const facebookAccounts = await facebookAccountService.getAnalyticsAccounts();

    if (facebookAccounts.length === 0) {
      console.warn('No Facebook accounts configured for analytics');
      return {};
    }

    console.log(`Fetching Facebook campaigns for ${facebookAccounts.length} accounts with forceRefresh=${forceRefresh}`);

    const allCampaigns = {};

    const accountPromises = facebookAccounts.map(async (account) => {
      try {
        const options = {
          onlyActive: false,  // Fetch all campaigns to show budget/actions for paused campaigns too
          activityDays: 30,
          limit: 5000  // High limit to capture all campaigns including older ones
        };

        const response = await Api.getFacebookCampaigns(forceRefresh, account.id, options);

        if (response && response.success && response.results && response.results.length > 0) {
          // Log first 5 campaign IDs from this account for debugging
          const sampleIds = response.results.slice(0, 5).map(c => c.id);
          const ids120236 = response.results.filter(c => c.id.startsWith('120236')).map(c => c.id);
          console.log(`Loaded ${response.results.length} campaigns from account ${account.name} (${account.id})`);
          console.log(`  Sample campaign IDs: ${sampleIds.join(', ')}`);
          if (ids120236.length > 0) {
            console.log(`  ⚠️ Found ${ids120236.length} campaigns starting with 120236: ${ids120236.slice(0, 5).join(', ')}`);
          }
          // Check specifically for the target campaign ID
          const targetFound = response.results.find(c => c.id === '120236832581510276');
          if (targetFound) {
            console.log(`  ✅ TARGET CAMPAIGN FOUND in account ${account.name}:`, targetFound);
          }

          response.results.forEach(campaign => {
            allCampaigns[campaign.id] = {
              ...campaign,
              account_id: account.id,
              account_name: account.name,
              is_active: campaign.status === 'ACTIVE',
              daily_budget: campaign.daily_budget,
              lifetime_budget: campaign.lifetime_budget,
              spend_cap: campaign.spend_cap,
              budget_remaining: campaign.budget_remaining,
              name: campaign.name || campaign.campaign_name || `Campaign ${campaign.id}`,
              display_name: `[${account.name}] ${campaign.name || campaign.campaign_name || `Campaign ${campaign.id}`}`
            };
          });
        }
      } catch (error) {
        console.error(`Error fetching campaigns for account ${account.name}:`, error);
      }
    });

    await Promise.all(accountPromises);

    console.log(`Total Facebook campaigns loaded: ${Object.keys(allCampaigns).length} from all accounts`);

    return { success: true, data: allCampaigns };
  } catch (error) {
    console.error('Error fetching Facebook campaigns:', error);
    return {
      success: false,
      error: error.message || 'Unknown error while fetching Facebook campaigns'
    };
  }
};

// Update campaign budget
export const updateCampaignBudget = async (campaign, newBudget) => {
  const campaignId = campaign?.campaign_id || campaign?.id;

  if (!campaign || !campaignId) {
    console.error('Invalid campaign data for budget update:', campaign);
    return {
      success: false,
      error: 'Invalid campaign data'
    };
  }

  try {
    const budget = parseFloat(newBudget);

    if (isNaN(budget)) {
      return {
        success: false,
        error: 'Invalid budget amount'
      };
    }

    // All campaigns are Facebook now
    const accountId = process.env.FACEBOOK_APP_ID || 'default_account_id';
    await Api.updateFacebookCampaignBudget(campaignId, budget, accountId);

    return { success: true };
  } catch (error) {
    console.error('Error updating campaign budget:', error);
    return {
      success: false,
      error: error.message || 'Failed to update campaign budget'
    };
  }
};

// Toggle campaign status (active/paused)
export const toggleCampaignStatus = async (campaignIdOrObject, isActiveOrCampaign, platformOrNothing) => {
  let campaign;

  if (typeof isActiveOrCampaign === 'boolean' && typeof platformOrNothing === 'string') {
    campaign = {
      campaign_id: campaignIdOrObject,
      is_active: isActiveOrCampaign,
      platform: platformOrNothing
    };
  } else if (typeof campaignIdOrObject === 'object') {
    campaign = campaignIdOrObject;
  } else {
    console.error('Invalid parameters for toggleCampaignStatus:', campaignIdOrObject, isActiveOrCampaign, platformOrNothing);
    return {
      success: false,
      error: 'Invalid campaign data'
    };
  }

  const campaignId = campaign?.campaign_id || campaign?.id;

  if (!campaignId) {
    console.error('Invalid campaign data for status toggle:', campaign);
    return {
      success: false,
      error: 'Invalid campaign data'
    };
  }

  try {
    const currentStatus = campaign.is_active || false;

    // All campaigns are Facebook now
    if (!currentStatus) {
      await Api.activateFacebookCampaign(campaignId);
    } else {
      await Api.pauseFacebookCampaign(campaignId);
    }

    return {
      success: true,
      newStatus: !currentStatus
    };
  } catch (error) {
    console.error('Error toggling campaign status:', error);
    return {
      success: false,
      error: error.message || 'Failed to toggle campaign status'
    };
  }
};

// Helper function to standardize numeric values in campaign data
const standardizeNumericValues = (campaigns) => {
  if (!campaigns || !Array.isArray(campaigns)) return campaigns;

  return campaigns.map(campaign => {
    if (!campaign) return campaign;

    const standardizedCampaign = { ...campaign };

    const numericFields = [
      'impressions', 'clicks', 'cost', 'revenue', 'profit',
      'roas', 'ctr', 'payout', 'estimated_revenue'
    ];

    numericFields.forEach(field => {
      if (standardizedCampaign[field] !== undefined) {
        if (typeof standardizedCampaign[field] === 'string') {
          const cleanValue = standardizedCampaign[field].replace(/[$,]/g, '');
          standardizedCampaign[field] = parseFloat(cleanValue) || 0;
        }
        else if (typeof standardizedCampaign[field] === 'number' && isNaN(standardizedCampaign[field])) {
          standardizedCampaign[field] = 0;
        }
      } else {
        standardizedCampaign[field] = 0;
      }
    });

    if (typeof standardizedCampaign.ctr === 'string' && standardizedCampaign.ctr.includes('%')) {
      standardizedCampaign.ctr = parseFloat(standardizedCampaign.ctr) || 0;
    }

    return standardizedCampaign;
  });
};

/**
 * Fetch complete campaign data from all sources:
 * - Facebook cost (traffic source)
 * - AdSense revenue (demand)
 * - Compado revenue (demand)
 */
export const fetchCampaignData = async (startDate, endDate, filters, viewState, sortOptions, paginationOptions, forceRefresh = false, facebookCampaigns = {}, facebookAccountId = null) => {
  try {
    console.log('🔧 fetchCampaignData called with:', {
      startDate: format(startDate, 'yyyy-MM-dd'),
      endDate: format(endDate, 'yyyy-MM-dd'),
      facebookAccountId,
      facebookCampaignsCount: Object.keys(facebookCampaigns).length,
      forceRefresh
    });

    const {
      sortField,
      sortDirection
    } = sortOptions;

    const initialViewState = { ...viewState };

    try {
      // Fetch all data sources in parallel
      const [facebookCostData, adSenseData, compadoData] = await Promise.all([
        fetchFacebookCost(startDate, endDate, forceRefresh, facebookAccountId),
        fetchAdSenseRevenue(startDate, endDate),
        fetchCompadoRevenue(startDate, endDate)
      ]);

      console.log('📊 Data fetched:', {
        facebookCost: facebookCostData.length,
        adSense: adSenseData.length,
        compado: compadoData.length
      });

      // Standardize numeric values
      const standardizedFacebookCost = standardizeNumericValues(facebookCostData);

      // Combine all data
      const combinedData = combineAllData(
        standardizedFacebookCost,
        adSenseData,
        compadoData,
        facebookCampaigns
      );

      if (!combinedData || !combinedData.campaigns || combinedData.campaigns.length === 0) {
        console.warn('No campaigns found after combining data');
        return {
          success: true,
          data: { campaigns: [] },
          totalItems: 0,
          allDataFetched: true,
          viewState: initialViewState
        };
      }

      // Sort campaigns
      let sortedCampaigns = [...combinedData.campaigns];
      if (sortField && sortDirection) {
        sortedCampaigns.sort((a, b) => {
          const aValue = a[sortField] || 0;
          const bValue = b[sortField] || 0;
          return sortDirection === 'desc' ? bValue - aValue : aValue - bValue;
        });
      }

      return {
        success: true,
        data: { campaigns: sortedCampaigns },
        facebookCostData: standardizedFacebookCost,
        adSenseData: adSenseData,
        compadoData: compadoData,
        totalItems: sortedCampaigns.length,
        allDataFetched: true,
        viewState: initialViewState
      };
    } catch (error) {
      console.error('Error fetching campaign data:', error);
      return {
        success: false,
        error: error.message || 'Failed to fetch campaign data',
        viewState: initialViewState
      };
    }
  } catch (error) {
    console.error('Error in fetchCampaignData:', error);
    return {
      success: false,
      error: error.message || 'Failed to fetch campaign data'
    };
  }
};

// Export data for CSV download
export const exportCampaignData = (startDate, endDate, filters, viewState, sortOptions) => {
  try {
    const { campaignFilter, groupBy } = filters;
    const { sortField, sortDirection } = sortOptions;

    const exportPayload = createExportPayload(
      startDate,
      endDate,
      campaignFilter,
      false,
      null,
      sortField,
      sortDirection,
      groupBy
    );

    return Api.getParkingEvents(exportPayload);
  } catch (error) {
    console.error('Error exporting campaign data:', error);
    throw error;
  }
};

// Sort campaigns by a specific field - client-side function
export const sortCampaignsByCost = (campaigns, direction = 'desc') => {
  if (!campaigns || !Array.isArray(campaigns) || campaigns.length === 0) {
    return campaigns;
  }

  return [...campaigns].sort((a, b) => {
    const costA = parseFloat(a.cost || 0);
    const costB = parseFloat(b.cost || 0);
    return direction === 'desc' ? costB - costA : costA - costB;
  });
};

// ============================================================
// DEPRECATED - Kept for backwards compatibility
// ============================================================

/**
 * @deprecated Taboola has been removed
 */
export const fetchTaboolaCampaigns = async () => {
  console.warn('fetchTaboolaCampaigns is deprecated - Taboola has been removed');
  return { success: true, data: {} };
};

/**
 * @deprecated Taboola has been removed
 */
export const loadBlacklistData = async () => {
  console.warn('loadBlacklistData is deprecated - Taboola has been removed');
  return { success: false, data: [] };
};

/**
 * @deprecated Taboola has been removed
 */
export const blacklistSite = async () => {
  console.warn('blacklistSite is deprecated - Taboola has been removed');
  return { success: false, error: 'Taboola has been removed' };
};

/**
 * @deprecated Taboola has been removed
 */
export const unblacklistSite = async () => {
  console.warn('unblacklistSite is deprecated - Taboola has been removed');
  return { success: false, error: 'Taboola has been removed' };
};
